
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box, Card, Typography, TextField, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Collapse, Paper,
    Chip, Grid, CircularProgress, Alert, Tooltip, MenuItem, Select, FormControl, InputLabel,
    Pagination
} from '@mui/material';
import {
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
    AccountTree as TreeIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    AccountTree as TreeViewIcon,
    AccountBalanceWallet as WalletIcon,
    Group as TeamIcon
} from '@mui/icons-material';
import { API_BASE } from '../config';

interface AuditNode {
    _id: string;
    email: string;
    emailOwner?: string; // for team
    emailMember?: string; // for team
    income: string;
    incomeDate: string;
    level?: number;
    remark?: string;
    plan: 'platinum' | 'normal';
    type: 'personal' | 'team';
    createdAt: string;
    children?: AuditNode[];
}

const Row = (props: { row: AuditNode }) => {
    const { row } = props;
    const [open, setOpen] = useState(false);

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' }, bgcolor: row.plan === 'platinum' ? 'rgba(103, 58, 183, 0.02)' : 'inherit' }}>
                <TableCell>
                    {row.children && row.children.length > 0 && (
                        <IconButton size="small" onClick={() => setOpen(!open)}>
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell>
                    <Typography variant="body2" fontWeight={600}>{row.email}</Typography>
                    <Typography variant="caption" color="text.secondary">{new Date(row.createdAt).toLocaleTimeString()}</Typography>
                </TableCell>
                <TableCell>
                    <Chip
                        label={row.plan.toUpperCase()}
                        size="small"
                        color={row.plan === 'platinum' ? 'secondary' : 'primary'}
                        variant="outlined"
                        sx={{ fontWeight: 700, borderRadius: 1 }}
                    />
                </TableCell>
                <TableCell>
                    <Typography variant="body2" fontWeight={700} color="success.main">
                        ${parseFloat(row.income).toFixed(2)}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" color="text.secondary">{row.remark || 'N/A'}</Typography>
                </TableCell>
                <TableCell align="right">
                    {row.children && row.children.length > 0 && (
                        <Chip
                            icon={<TeamIcon sx={{ fontSize: '12px !important' }} />}
                            label={`${row.children.length} Rewards`}
                            size="small"
                            sx={{ bgcolor: 'info.50', color: 'info.dark', fontWeight: 600 }}
                        />
                    )}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2, mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mb: 2 }}>
                                <TreeViewIcon fontSize="small" /> Team Commission Breakdown
                            </Typography>
                            <Table size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                                        <TableCell><Typography variant="caption" fontWeight={700}>Sponsor Email</Typography></TableCell>
                                        <TableCell><Typography variant="caption" fontWeight={700}>Level</Typography></TableCell>
                                        <TableCell><Typography variant="caption" fontWeight={700}>Amount</Typography></TableCell>
                                        <TableCell><Typography variant="caption" fontWeight={700}>Remark</Typography></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {row.children?.map((child) => (
                                        <TableRow key={child._id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell><Typography variant="body2">{child.emailOwner}</Typography></TableCell>
                                            <TableCell>
                                                <Chip label={`L${child.level}`} size="small" sx={{ height: 20, fontSize: '10px', fontWeight: 700 }} />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={700} color="info.main">${parseFloat(child.income).toFixed(2)}</Typography>
                                            </TableCell>
                                            <TableCell><Typography variant="caption" color="text.secondary">{child.remark}</Typography></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

const CronAudit: React.FC = () => {
    const [data, setData] = useState<AuditNode[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [email, setEmail] = useState('');
    const [plan, setPlan] = useState('all');
    const [stats, setStats] = useState<any>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const [treeRes, statsRes] = await Promise.all([
                axios.get(`${API_BASE}/api/audit/cron-tree`, {
                    params: { date, email: email.trim(), plan, page, limit: 50 },
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_BASE}/api/audit/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            setData(treeRes.data.data || []);
            setTotalPages(treeRes.data.pagination?.totalPages || 1);
            setStats(statsRes.data.data);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to fetch audit logs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page]);

    return (
        <Box sx={{ p: 4 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" fontWeight={800} gutterBottom>Cron Audit Logs</Typography>
                    <Typography variant="body1" color="text.secondary">Detailed flow of funds and multi-level rewards</Typography>
                </Box>
                <Button
                    startIcon={<RefreshIcon />}
                    variant="contained"
                    onClick={fetchData}
                    disabled={loading}
                    sx={{ borderRadius: 2, px: 3 }}
                >
                    Refresh
                </Button>
            </Box>

            {stats && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={3}>
                        <Card sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'secondary.50' }}>
                            <Typography variant="caption" fontWeight={700} color="secondary.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <WalletIcon sx={{ fontSize: 16 }} /> PLATINUM PERSONAL (TODAY)
                            </Typography>
                            <Typography variant="h5" fontWeight={800}>${stats.platinum.personalSum.toFixed(2)}</Typography>
                            <Typography variant="caption" color="text.secondary">{stats.platinum.personal} Payouts</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'error.50' }}>
                            <Typography variant="caption" fontWeight={700} color="error.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TeamIcon sx={{ fontSize: 16 }} /> PLATINUM TEAM (TODAY)
                            </Typography>
                            <Typography variant="h5" fontWeight={800}>${(stats.platinum?.teamSum || 0).toFixed(2)}</Typography>
                            <Typography variant="caption" color="text.secondary">{stats.platinum?.team || 0} Rewards</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'primary.50' }}>
                            <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <WalletIcon sx={{ fontSize: 16 }} /> NORMAL PERSONAL (TODAY)
                            </Typography>
                            <Typography variant="h5" fontWeight={800}>${(stats.normal?.personalSum || 0).toFixed(2)}</Typography>
                            <Typography variant="caption" color="text.secondary">{stats.normal?.personal || 0} Payouts</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'warning.50' }}>
                            <Typography variant="caption" fontWeight={700} color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TeamIcon sx={{ fontSize: 16 }} /> NORMAL TEAM (TODAY)
                            </Typography>
                            <Typography variant="h5" fontWeight={800}>${(stats.normal?.teamSum || 0).toFixed(2)}</Typography>
                            <Typography variant="caption" color="text.secondary">{stats.normal?.team || 0} Rewards</Typography>
                        </Card>
                    </Grid>
                </Grid>
            )}

            <Card sx={{ p: 3, mb: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <TextField
                            label="Filter Date"
                            type="date"
                            fullWidth
                            size="small"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            label="User Email"
                            placeholder="Search by email..."
                            fullWidth
                            size="small"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Investment Plan</InputLabel>
                            <Select
                                value={plan}
                                label="Investment Plan"
                                onChange={(e) => setPlan(e.target.value)}
                            >
                                <MenuItem value="all">All Plans</MenuItem>
                                <MenuItem value="platinum">Platinum Only</MenuItem>
                                <MenuItem value="normal">Normal Only</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<SearchIcon />}
                            onClick={fetchData}
                            sx={{ height: 40, borderRadius: 2 }}
                        >
                            Filter
                        </Button>
                    </Grid>
                </Grid>
            </Card>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell width={50} />
                            <TableCell><Typography fontWeight={700}>Investor</Typography></TableCell>
                            <TableCell><Typography fontWeight={700}>Plan</Typography></TableCell>
                            <TableCell><Typography fontWeight={700}>Income</Typography></TableCell>
                            <TableCell><Typography fontWeight={700}>Details / Remark</Typography></TableCell>
                            <TableCell align="right"><Typography fontWeight={700}>Team Impact</Typography></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                    <CircularProgress size={30} sx={{ mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary">Analyzing cron tree...</Typography>
                                </TableCell>
                            </TableRow>
                        ) : data.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                    <Typography color="text.secondary">No payouts found for the selected filters.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            data.map((row) => <Row key={row._id} row={row} />)
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                    color="primary"
                    shape="rounded"
                    size="large"
                />
            </Box>
        </Box>
    );
};

export default CronAudit;
